import React, {useEffect, useMemo, useState} from "react";
import {
  Box, Button, CircularProgress, Dialog, IconButton, InputBase, ListSubheader, MenuItem, Select, Stack, Tab, Tabs,
  Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon} from "@hugeicons/core-free-icons";
import {AmiRepository} from "../../repositories/AmiRepository";
import {getTarget} from "../../pages/project/dialplan/components/SubProjectFields";
import {subProjectLabel} from "../../utils/subProjects";
import {useWebrtc} from "../../context/WebrtcContext";

const INIT_MESSAGE_MAX = 2000;

const labelSx = {fontSize: 12, fontWeight: 600, color: "#4A4A63", mb: 0.75};
const controlSx = {
  display: "flex", alignItems: "center", minHeight: 40, px: 1.5, borderRadius: "10px", border: "1px solid #E4E4EE",
  bgcolor: "#fff", fontSize: 13, color: "#23264D",
};
const buttonSx = {height: 36, px: 2, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600};

// Redirect the answered call (see redirectCall in WebrtcProvider): to a sub project's bot extension from the
// dialplan, with an optional first message for the bot, or to a number (VDN, phone, agent or group number).
export const RedirectCallDialog = ({open, onClose, call}) => {
  const {redirectCall} = useWebrtc();
  const [mode, setMode] = useState("bot");
  const [extensions, setExtensions] = useState(null);
  const [extensionId, setExtensionId] = useState("");
  const [initMessage, setInitMessage] = useState("");
  const [number, setNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const subProjects = useMemo(() => call?.subProjects || [], [call?.subProjects]);

  useEffect(() => {
    if (!open) return;
    setMode("bot");
    setExtensionId("");
    setInitMessage("");
    setNumber("");
    setExtensions(null);
    if (!call?.projectId) return;
    let alive = true;
    AmiRepository.listExtensions(call.projectId)
      .then(({data}) => alive && setExtensions((data?.extensions || [])
        .filter(ext => ext.enabled !== false && ext.id)
        .sort((a, b) => String(a.extension).localeCompare(String(b.extension), undefined, {numeric: true}))))
      .catch(e => {
        console.error("Failed to load the extensions", e);
        if (alive) setExtensions([]);
      });
    return () => {
      alive = false;
    };
  }, [open, call?.projectId]);

  // the dialplan's extensions by the sub project that owns them
  const groups = useMemo(() => {
    const bySubProject = new Map();
    (extensions || []).forEach(ext => {
      const subProject = subProjects.find(item => String(item.id) === String(getTarget(ext, subProjects).subProjectId));
      const label = subProject ? subProjectLabel(subProject) : "Other";
      bySubProject.set(label, [...(bySubProject.get(label) || []), ext]);
    });
    return [...bySubProject.entries()];
  }, [extensions, subProjects]);

  const valid = mode === "bot" ? Boolean(extensionId) : Boolean(number.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    const success = await redirectCall(mode === "bot"
      ? {extensionId, initMessage: initMessage.trim()}
      : {number: number.trim()});
    setSaving(false);
    if (success) onClose();
  };

  return (
    // above the call popup (zIndex 1400)
    <Dialog open={open} onClose={saving ? undefined : onClose} sx={{zIndex: 1500}}
            PaperProps={{component: "form", onSubmit: handleSubmit, sx: {borderRadius: "20px", width: "min(460px, calc(100vw - 32px))", m: 2, p: {xs: 2.5, md: 3}}}}>
      <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
        <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D"}}>Redirect call</Typography>
        <IconButton size={"small"} onClick={onClose} disabled={saving} sx={{color: "#8A8AA0"}}>
          <HugeiconsIcon icon={Cancel01Icon} size={18}/>
        </IconButton>
      </Stack>
      <Typography sx={{fontSize: 13, color: "#8A8AA0", mt: 0.5}}>
        The caller is sent to the new destination and your call ends.
      </Typography>

      <Tabs
        value={mode} onChange={(e, value) => setMode(value)}
        sx={{
          mt: 1.5, mb: 2, minHeight: 40, borderBottom: "1px solid #ECECF3",
          "& .MuiTab-root": {textTransform: "none", minHeight: 40, minWidth: "auto", fontWeight: 700, fontSize: 13, px: 0, mr: 3, color: "#8A8AA0", "&.Mui-selected": {color: "#8491F7"}},
          "& .MuiTabs-indicator": {backgroundColor: "#8491F7"},
        }}
      >
        <Tab value={"bot"} label={"Sub project (bot)"}/>
        <Tab value={"number"} label={"Extension / VDN"}/>
      </Tabs>

      {mode === "bot" ? (
        <Stack gap={2}>
          <Box>
            <Typography sx={labelSx}>Dialplan extension</Typography>
            {extensions === null ? (
              <Box sx={{...controlSx, justifyContent: "center"}}><CircularProgress size={16}/></Box>
            ) : (
              <Select
                variant={"standard"} disableUnderline fullWidth displayEmpty value={extensionId}
                onChange={e => setExtensionId(e.target.value)}
                MenuProps={{sx: {zIndex: 1600}, PaperProps: {sx: {borderRadius: "12px", maxHeight: 320}}}}
                sx={{...controlSx, "& .MuiSelect-select": {py: 0}}}
              >
                <MenuItem value={""} disabled sx={{fontSize: 13}}>
                  {extensions.length ? "Select an extension" : "No active extensions in the dialplan"}
                </MenuItem>
                {groups.flatMap(([label, items]) => [
                  <ListSubheader key={`h-${label}`} sx={{fontSize: 11, fontWeight: 700, lineHeight: "28px", color: "#8491F7", textTransform: "uppercase"}}>
                    {label}
                  </ListSubheader>,
                  ...items.map(ext => (
                    <MenuItem key={ext.id} value={ext.id} sx={{fontSize: 13}}>
                      <Box component={"span"} sx={{fontFamily: "monospace", mr: 1}}>{ext.extension}</Box>
                      {ext.name || ext.description || ""}
                    </MenuItem>
                  )),
                ])}
              </Select>
            )}
          </Box>
          <Box>
            <Typography sx={labelSx}>Message for the bot (optional)</Typography>
            <Box sx={{...controlSx, alignItems: "flex-start", py: 1}}>
              <InputBase
                multiline minRows={3} maxRows={6} fullWidth value={initMessage}
                onChange={e => setInitMessage(e.target.value.slice(0, INIT_MESSAGE_MAX))}
                placeholder={"e.g. The caller wants to change their delivery address"}
                sx={{fontSize: 13, color: "#23264D"}}
              />
            </Box>
            <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.75}}>
              The bot gets it as the first message of its new conversation. {initMessage.length}/{INIT_MESSAGE_MAX}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Box>
          <Typography sx={labelSx}>Number</Typography>
          <Box sx={controlSx}>
            <InputBase
              autoFocus fullWidth value={number} onChange={e => setNumber(e.target.value)} placeholder={"963"}
              inputProps={{inputMode: "tel"}} sx={{fontSize: 13, color: "#23264D"}}
            />
          </Box>
          <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.75}}>
            A VDN, phone number, agent number (8XXXXXX) or agent group number (7XXXXXX).
          </Typography>
        </Box>
      )}

      <Stack direction={"row"} justifyContent={"flex-end"} gap={1.5} sx={{mt: 3}}>
        <Button type={"button"} onClick={onClose} disabled={saving} sx={{...buttonSx, color: "#374151"}}>Cancel</Button>
        <Button
          type={"submit"} disabled={!valid || saving}
          startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : null}
          sx={{...buttonSx, color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "&.Mui-disabled": {bgcolor: "#E4E4EE", color: "#A0A0B4"}}}
        >
          Redirect
        </Button>
      </Stack>
    </Dialog>
  );
};
